import { batch } from 'react-redux';
import { createLogic } from 'redux-logic';

import { AuthActions, AuthActionType } from 'common/auth/actions';
import { getPostLoginRedirect } from 'common/auth/postLoginRedirect';
import { userMustAcceptPrivacyPolicy } from 'common/auth/privacyPolicyAcceptance';
import type { SsoSessionKeys } from 'common/auth/sso/sso.types';
import type { ActiveStudySite, AuthUser } from 'common/auth/types';
import { getCurrentLng, i18n } from 'common/i18n/i18n';
import { CILogger } from 'common/log/log.provider';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import oktaAuth from 'module/auth/okta/oktaAuth';
import { guardedOktaSignInWithRedirect } from 'module/auth/okta/utils/oktaUtils';

import { DeviceHandoffActions } from '../state/actions';
import { DELAY_OKTA_REDIRECT_TO_SHOW_HANDBACK_NOTICE_MS, DeviceHandoffActionType } from '../state/constants';
import { persistDeviceHandoff } from '../state/persistence';
import { selectHandoffActiveStudySite, selectIsSwitchingToSiteUser } from '../state/selectors';
import type { DeviceHandoffRootState } from '../state/selectors';
import type { DeviceHandoffEvent, DeviceHandoffRejectionReason, DeviceHandoffState } from '../state/types';

import { evaluateDeviceHandoffCandidate } from './candidatePolicy';
import type { DeviceHandoffDecision } from './candidatePolicy';
import {
    acceptIncomingHandoffUser,
    authenticateIncomingHandoffUser,
    rejectIncomingHandoffUser,
    type AuthDispatch,
} from './incomingHandoffUser';
import { deviceHandoffPersistenceLogic } from './persistenceLogic';
import { writeRespondentActivationDate } from './respondentActivation';

interface DeviceHandoffOrchestratorRootState extends DeviceHandoffRootState {
    auth: { activeStudySite: ActiveStudySite; activeUser: AuthUser | null };
}

const DeviceHandoffIntent = {
    SUBMIT_CREDENTIALS: 'deviceHandoff/intent/SUBMIT_CREDENTIALS',
} as const;

interface SubmitCredentialsPayload {
    username: string;
    password: string;
    ssoTokens?: SsoSessionKeys;
}

function submitCredentials(username: string | null, password: string | null, ssoTokens?: SsoSessionKeys) {
    return {
        type: DeviceHandoffIntent.SUBMIT_CREDENTIALS,
        payload: { username, password, ssoTokens } as SubmitCredentialsPayload,
    };
}

export const DeviceHandoffOrchestratorActions = { submitCredentials };

function isSiteUserReturning(deviceHandoffState: DeviceHandoffState): boolean {
    return deviceHandoffState.phase === 'switch_to_siteuser';
}

function loginFailureReason(deviceHandoffState: DeviceHandoffState): DeviceHandoffRejectionReason {
    return isSiteUserReturning(deviceHandoffState) ? 'userNotAuthorized' : 'respondentNotAuthorized';
}

function resolveHandoffEventForAuthenticatedUser(
    deviceHandoffState: DeviceHandoffState,
    incomingUser: AuthUser
): DeviceHandoffEvent {
    const isFirstTimeRespondentPostActivation =
        deviceHandoffState.phase === 'switch_to_respondent' && deviceHandoffState.step.type === 'activating';

    if (isSiteUserReturning(deviceHandoffState)) {
        return DeviceHandoffActions.siteUserReturned();
    }

    if (userMustAcceptPrivacyPolicy(incomingUser)) {
        return DeviceHandoffActions.policyAcceptanceRequired();
    }

    if (isFirstTimeRespondentPostActivation) {
        return DeviceHandoffActions.activationCompleted();
    }

    return DeviceHandoffActions.respondentAuthenticated();
}

async function authorizeIncomingHandoffUser(
    incomingUser: AuthUser,
    deviceHandoffState: DeviceHandoffState,
    activeStudySite: ActiveStudySite,
    dispatch: AuthDispatch
): Promise<void> {
    const decision: DeviceHandoffDecision = evaluateDeviceHandoffCandidate(
        deviceHandoffState,
        incomingUser,
        activeStudySite
    );

    if (decision.outcome === 'rejected') {
        await rejectIncomingHandoffUser(incomingUser, decision.reason);
        dispatch(DeviceHandoffActions.rejected(decision.reason));
    } else {
        batch(() => {
            acceptIncomingHandoffUser(dispatch, incomingUser, activeStudySite, isSiteUserReturning(deviceHandoffState));
            dispatch(resolveHandoffEventForAuthenticatedUser(deviceHandoffState, incomingUser));
        });
    }
}

async function handleSubmitCredentials(
    payload: SubmitCredentialsPayload,
    deviceHandoffState: DeviceHandoffState,
    activeStudySite: ActiveStudySite,
    dispatch: AuthDispatch
): Promise<void> {
    let incomingUser: AuthUser;

    try {
        incomingUser = await authenticateIncomingHandoffUser(payload.username, payload.password, payload.ssoTokens);
    } catch (error) {
        CILogger.getInstance().error('device handoff: incoming user authentication failed', error);
        dispatch(AuthActions.loginFailed(error));
        dispatch(DeviceHandoffActions.rejected(loginFailureReason(deviceHandoffState)));

        return;
    }

    await authorizeIncomingHandoffUser(incomingUser, deviceHandoffState, activeStudySite, dispatch);
}

let turnEndedAt: number | null = null;

const LogOutAtHandoffBoundaryLogic = createLogic<DeviceHandoffOrchestratorRootState>({
    type: [DeviceHandoffActionType.INITIATE, DeviceHandoffActionType.RESPONDENT_TURN_ENDED],
    process({ getState }, dispatch, done): void {
        turnEndedAt = Date.now();
        persistDeviceHandoff(getState().deviceHandoff);
        dispatch(AuthActions.logOutPreservingDeviceHandoffState());
        done();
    },
});

const AuthenticateDeviceHandoffCandidateLogic = createLogic<DeviceHandoffOrchestratorRootState>({
    type: DeviceHandoffIntent.SUBMIT_CREDENTIALS,
    process({ getState, action }, dispatch, done): void {
        const { payload } = action as unknown as { payload: SubmitCredentialsPayload };
        const { deviceHandoff, auth } = getState();
        const activeStudySite = selectHandoffActiveStudySite({ deviceHandoff }) ?? auth.activeStudySite;

        handleSubmitCredentials(payload, deviceHandoff, activeStudySite, dispatch).finally(done);
    },
});

function beginRespondentActivation(dispatch: AuthDispatch, respondent: AuthUser, isCaregiver: boolean): Promise<void> {
    dispatch(DeviceHandoffActions.startActivation());

    return writeRespondentActivationDate(respondent, isCaregiver)
        .then((): void => {
            dispatch(DeviceHandoffActions.accountCreated());
        })
        .catch((error): void => {
            CILogger.getInstance().error('device handoff: failed to write respondent activation date', error);
            const t = i18n.getFixedT(getCurrentLng(), 'global');
            CIPageStatus.error(t('graphql.systemerror'));
        });
}

function resolveFirstTimeRespondent(state: DeviceHandoffOrchestratorRootState): AuthUser | null {
    const { deviceHandoff, auth } = state;

    if (deviceHandoff.phase !== 'switch_to_respondent' || deviceHandoff.step.type !== 'authenticating') {
        return null;
    }

    const isActiveUserFirstTimeRespondent =
        !deviceHandoff.target.activationStatus &&
        !!auth.activeUser &&
        auth.activeUser.pk === deviceHandoff.target.respondentPK;

    return isActiveUserFirstTimeRespondent ? auth.activeUser : null;
}

const StartRespondentActivationOnFirstPasswordLogic = createLogic<DeviceHandoffOrchestratorRootState>({
    type: AuthActionType.LOGIN_SUCCESS,
    process({ getState }, dispatch, done): void {
        const respondent = resolveFirstTimeRespondent(getState());

        if (!respondent) {
            done();

            return;
        }

        const { deviceHandoff } = getState();
        const isCaregiver = deviceHandoff.phase === 'switch_to_respondent' && !!deviceHandoff.target.isCaregiverUser;

        beginRespondentActivation(dispatch, respondent, isCaregiver).finally(done);
    },
});

const ReturnDeviceToClinicianLogic = createLogic<DeviceHandoffOrchestratorRootState>({
    type: AuthActionType.LOGOUT_SUCCESS,
    process({ getState }, _dispatch, done): void {
        const isReturningTheDevice = selectIsSwitchingToSiteUser(getState()) && Boolean(getPostLoginRedirect());

        if (!isReturningTheDevice) {
            done();

            return;
        }

        const elapsed =
            turnEndedAt === null ? DELAY_OKTA_REDIRECT_TO_SHOW_HANDBACK_NOTICE_MS : Date.now() - turnEndedAt;
        const remainingNoticeMs = Math.max(0, DELAY_OKTA_REDIRECT_TO_SHOW_HANDBACK_NOTICE_MS - elapsed);

        setTimeout(() => {
            turnEndedAt = null;
            guardedOktaSignInWithRedirect(oktaAuth)
                .catch((error) => CILogger.getInstance().error('Error initiating Okta redirect:', error))
                .finally(done);
        }, remainingNoticeMs);
    },
});

const SESSION_ENDINGS_OUTSIDE_HANDOFF = [AuthActionType.LOGOUT_REQUEST, AuthActionType.RESET_USER_DATA];

const ResetHandoffOnSessionEndLogic = createLogic<DeviceHandoffOrchestratorRootState>({
    type: SESSION_ENDINGS_OUTSIDE_HANDOFF,
    process(_deps, dispatch, done): void {
        dispatch(DeviceHandoffActions.reset());
        done();
    },
});

export const deviceHandoffLogic = [
    LogOutAtHandoffBoundaryLogic,
    ReturnDeviceToClinicianLogic,
    AuthenticateDeviceHandoffCandidateLogic,
    StartRespondentActivationOnFirstPasswordLogic,
    ResetHandoffOnSessionEndLogic,
    ...deviceHandoffPersistenceLogic,
];
